import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";

const statusColors = {
  matched: "success",
  no_match: "default",
  error: "error",
  disabled: "default",
  done: "success",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch (err) {
    return String(value);
  }
}

const PlannedActions = ({ actions = [] }) => (
  <Stack spacing={1} divider={<Divider flexItem />}>
    {actions.length === 0 ? (
      <Typography variant="body2" color="textSecondary">
        No planned actions.
      </Typography>
    ) : (
      actions.map((action, idx) => (
        <Box key={`${action.type || "action"}-${idx}`}>
          <Typography variant="subtitle2">{action.type || "Action"}</Typography>
          <Typography variant="body2" color="textSecondary">
            {action.enabled === false ? "Disabled" : "Configured"}
          </Typography>
          {action.config && (
            <pre className="ai-prompt-text" style={{ marginTop: 6 }}>
              {JSON.stringify(action.config, null, 2)}
            </pre>
          )}
        </Box>
      ))
    )}
  </Stack>
);

const InputPreview = ({ preview = {} }) => {
  const identity = preview.identity || {};
  const inbound = preview?.raw?.inbound || {};

  return (
    <Stack spacing={1.25}>
      <div>
        <Typography variant="subtitle2">Identity</Typography>
        <Typography variant="body2" color="textSecondary">
          {identity.name || "Unnamed"}
          {identity.email ? ` • ${identity.email}` : ""}
          {identity.phone ? ` • ${identity.phone}` : ""}
        </Typography>
      </div>
      <div>
        <Typography variant="subtitle2">Inbound</Typography>
        <Typography variant="body2" color="textSecondary">
          Form: {inbound.form_name || inbound.form_id || "—"}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Page: {inbound.page_url || "—"}
        </Typography>
      </div>
    </Stack>
  );
};

const DEFAULT_PACK_KEY = "distributor-defaults";

const AiTriggersTab = ({ instanceUniqueId }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [settings, setSettings] = useState({ enabled: true, packs: [], overrides: {} });
  const [packs, setPacks] = useState([]);
  const [packRules, setPackRules] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testPayload, setTestPayload] = useState("{}");
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/triggers/runs?limit=50`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const payload = await response.json();
      if (payload?.success) {
        setRuns(payload.rows || []);
      } else {
        setError(payload?.message || "Unable to load trigger runs.");
      }
    } catch (err) {
      setError("Unable to load trigger runs.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/triggers/settings`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const payload = await response.json();
      if (payload?.success) {
        const loaded = payload.settings || {};
        setSettings({
          enabled: loaded.enabled !== false,
          packs: Array.isArray(loaded.packs) && loaded.packs.length ? loaded.packs : [DEFAULT_PACK_KEY],
          overrides: loaded.overrides || {},
        });
      }
    } catch (err) {
      // ignore
    } finally {
      setSettingsLoading(false);
    }
  }, [instanceUniqueId]);

  const fetchPacks = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/triggers/packs`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      const payload = await response.json();
      if (payload?.success) {
        setPacks(payload.packs || []);
      }
    } catch (err) {
      // ignore
    }
  }, []);

  const fetchRulesForPack = useCallback(
    async (packKey) => {
      if (!packKey || packRules[packKey]) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/triggers/packs/${packKey}/rules`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        const payload = await response.json();
        if (payload?.success) {
          setPackRules((prev) => ({ ...prev, [packKey]: payload.rules || [] }));
        }
      } catch (err) {
        // ignore
      }
    },
    [packRules]
  );

  useEffect(() => {
    fetchRuns();
    fetchSettings();
    fetchPacks();
  }, [fetchRuns, fetchSettings, fetchPacks]);

  useEffect(() => {
    (settings.packs || []).forEach((packKey) => fetchRulesForPack(packKey));
  }, [settings.packs, fetchRulesForPack]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/triggers/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify(settings),
        }
      );
      const payload = await response.json();
      if (payload?.success) {
        setSettings((prev) => ({ ...prev, ...(payload.settings || {}) }));
      }
    } catch (err) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const togglePack = (packKey) => {
    setSettings((prev) => {
      const packs = new Set(prev.packs || []);
      if (packs.has(packKey)) {
        packs.delete(packKey);
      } else {
        packs.add(packKey);
      }
      const nextPacks = Array.from(packs);
      return { ...prev, packs: nextPacks.length ? nextPacks : [DEFAULT_PACK_KEY] };
    });
  };

  const updateOverride = (ruleKey, updater) => {
    setSettings((prev) => {
      const nextOverrides = { ...(prev.overrides || {}) };
      const current = nextOverrides[ruleKey] || {};
      const updated = updater(current);
      if (updated && Object.keys(updated).length) {
        nextOverrides[ruleKey] = updated;
      } else {
        delete nextOverrides[ruleKey];
      }
      return { ...prev, overrides: nextOverrides };
    });
  };

  const handleRuleEnabledToggle = (rule, enabled) => {
    updateOverride(rule.id, (current) => ({ ...current, enabled }));
  };

  const handleFormIdOverrideChange = (rule, value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      updateOverride(rule.id, (current) => {
        const next = { ...current };
        delete next.conditions;
        return next;
      });
      return;
    }
    updateOverride(rule.id, (current) => ({
      ...current,
      conditions: [{ path: "raw.inbound.form_id", op: "eq", value: trimmed }],
    }));
  };

  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <Stack direction="row" alignItems="center" spacing={1.5} padding={2}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Loading trigger runs...
          </Typography>
        </Stack>
      );
    }

    if (error) {
      return (
        <Typography variant="body2" color="error" padding={2}>
          {error}
        </Typography>
      );
    }

    if (!runs.length) {
      return (
        <Typography variant="body2" color="textSecondary" padding={2}>
          No trigger runs yet.
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Matched rules</TableCell>
            <TableCell align="right">Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run) => (
            <TableRow key={run.id} hover>
              <TableCell>{formatDate(run.createdAt)}</TableCell>
              <TableCell>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {run.eventType}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {run.eventKey || "—"}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip
                  label={run.status}
                  size="small"
                  color={statusColors[run.status] || "default"}
                  variant={run.status === "no_match" ? "outlined" : "filled"}
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {(run.result?.matchedRules || []).map((rule) => (
                    <Chip key={rule.ruleKey || rule.id} label={rule.ruleKey || rule.id} size="small" />
                  ))}
                  {!run.result?.matchedRules?.length && (
                    <Typography variant="caption" color="textSecondary">
                      None
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => setSelectedRun(run)}>
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }, [error, loading, runs]);

  const renderPackRules = () => {
    if (!settings.packs.length) return null;
    const allRules = settings.packs.flatMap((packKey) => packRules[packKey] || []);

    if (!allRules.length) {
      return (
        <Typography variant="body2" color="textSecondary">
          No rules available for selected packs.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5}>
        {allRules.map((rule) => {
          const override = settings.overrides?.[rule.id] || {};
          const enabled = override.enabled !== undefined ? override.enabled : rule.enabled !== false;
          const hasFormCondition = (rule.conditions || []).some((c) => String(c.path || "").includes("raw.inbound.form_id"));
          const formOverrideValue = override.conditions?.[0]?.value || "";
          return (
            <Paper variant="outlined" key={rule.id} sx={{ p: 1.25 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <div>
                  <Typography variant="subtitle2">{rule.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {rule.id}
                  </Typography>
                </div>
                <FormControlLabel
                  control={<Switch checked={enabled} onChange={(e) => handleRuleEnabledToggle(rule, e.target.checked)} />}
                  label="Enabled"
                />
              </Stack>
              {hasFormCondition && (
                <TextField
                  fullWidth
                  margin="dense"
                  label="Form ID override"
                  placeholder="logo-upload"
                  value={formOverrideValue}
                  onChange={(e) => handleFormIdOverrideChange(rule, e.target.value)}
                  size="small"
                />
              )}
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const handleTestRun = async () => {
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      let parsed = {};
      try {
        parsed = testPayload ? JSON.parse(testPayload) : {};
      } catch (err) {
        setTestError("Payload must be valid JSON");
        setTestLoading(false);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/triggers/test-lead`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({ payload: parsed }),
        }
      );
      const payload = await response.json();
      if (payload?.success) {
        setTestResult(payload.result || payload.run);
        fetchRuns();
      } else {
        setTestError(payload?.message || "Test failed");
      }
    } catch (err) {
      setTestError("Test failed");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <div>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            AI Triggers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Dry-run trigger evaluations for this instance. Actions are not executed yet.
          </Typography>
        </div>
        <Stack direction="row" spacing={1} alignItems="center">
          {error && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchRuns}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRuns} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Paper variant="outlined" className="ai-table-card">
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Trigger Settings
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => setSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Triggers Enabled"
            />
          </Stack>
          <Typography variant="body2" color="textSecondary" mb={1}>
            Select the trigger packs to apply to this instance and override rule-level behavior.
          </Typography>
          <Stack spacing={1}>
            {(packs || []).map((pack) => (
              <FormControlLabel
                key={pack.packKey}
                control={
                  <Checkbox
                    checked={(settings.packs || []).includes(pack.packKey)}
                    onChange={() => togglePack(pack.packKey)}
                  />
                }
                label={`${pack.name} (${pack.packKey})`}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} mt={2}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSettings} disabled={saving || settingsLoading}>
              Save Settings
            </Button>
            {saving && <CircularProgress size={18} />}
          </Stack>
        </Paper>

        <Paper variant="outlined" className="ai-table-card">
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Pack Rules
          </Typography>
          {renderPackRules()}
        </Paper>

        <Paper variant="outlined" className="ai-table-card">
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Test Trigger
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={1}>
            Run a dry test for "New Lead Submitted" with a JSON payload.
          </Typography>
          <TextField
            label="Lead payload (JSON)"
            value={testPayload}
            onChange={(e) => setTestPayload(e.target.value)}
            minRows={5}
            multiline
            fullWidth
          />
          <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
            <Button variant="contained" onClick={handleTestRun} disabled={testLoading}>
              Run Dry Test (New Lead Submitted)
            </Button>
            {testLoading && <CircularProgress size={18} />}
            {testError && (
              <Typography variant="body2" color="error">
                {testError}
              </Typography>
            )}
          </Stack>
          {testResult && (
            <Box mt={2}>
              <Typography variant="subtitle2">Result</Typography>
              <pre className="ai-prompt-text" style={{ marginTop: 6 }}>
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" className="ai-table-card">
          {tableContent}
        </Paper>
      </Stack>

      <Dialog open={Boolean(selectedRun)} onClose={() => setSelectedRun(null)} maxWidth="md" fullWidth>
        <DialogTitle>Trigger run details</DialogTitle>
        <DialogContent dividers>
          {selectedRun && (
            <Stack spacing={2}>
              <div>
                <Typography variant="subtitle2">Event</Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedRun.eventType} {selectedRun.eventKey ? `• ${selectedRun.eventKey}` : ""}
                </Typography>
              </div>

              <div>
                <Typography variant="subtitle2">Matched rules</Typography>
                {(selectedRun.result?.matchedRules || []).length ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {selectedRun.result.matchedRules.map((rule) => (
                      <Chip key={rule.ruleKey || rule.id} label={`${rule.ruleKey || rule.id} (${rule.priority})`} size="small" />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No matches.
                  </Typography>
                )}
              </div>

              <div>
                <Typography variant="subtitle2">Input preview</Typography>
                <InputPreview preview={selectedRun.inputPreview} />
              </div>

              <div>
                <Typography variant="subtitle2">Planned actions</Typography>
                <PlannedActions actions={selectedRun.result?.plannedActions} />
              </div>

              {selectedRun.stopReason && (
                <div>
                  <Typography variant="subtitle2">Stop reason</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedRun.stopReason}
                  </Typography>
                </div>
              )}

              {selectedRun.error && (
                <div>
                  <Typography variant="subtitle2" color="error">
                    Error
                  </Typography>
                  <Typography variant="body2" color="error">
                    {selectedRun.error}
                  </Typography>
                </div>
              )}

              <Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="textSecondary">
                  Runs are deduped by event + payload identity per minute and stored as dry runs only.
                </Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AiTriggersTab;
